import React from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';

/**
 * Modale di avviso al salvataggio per indirizzi email non validi o con potenziali refusi.
 * Non blocca mai il salvataggio: offre la possibilità di correggere manualmente o procedere comunque.
 */
const EmailWarningModal = ({
  show,
  warnings = [],
  onCorrect,
  onIgnoreAndProceed,
}) => {
  if (!show || !warnings || warnings.length === 0) {
    return null;
  }

  const handleCorrect = () => {
    if (onCorrect) {
      onCorrect();
    }

    const firstWarning = warnings[0];
    if (firstWarning) {
      setTimeout(() => {
        let targetElement = null;
        if (firstWarning.fieldId) {
          targetElement = document.getElementById(firstWarning.fieldId);
        }
        if (!targetElement && firstWarning.selector) {
          targetElement = document.querySelector(firstWarning.selector);
        }
        if (targetElement) {
          if (typeof targetElement.scrollIntoView === 'function') {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          if (typeof targetElement.focus === 'function') {
            targetElement.focus();
          }
        }
      }, 150);
    }
  };

  const isSingle = warnings.length === 1;

  return (
    <Modal
      show={show}
      onHide={handleCorrect}
      centered
      backdrop="static"
      keyboard={false}
      data-testid="email-warning-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center text-dark fs-5">
          <i className="fas fa-exclamation-triangle text-warning me-2 fs-4"></i>
          <span>Verifica Indirizzo Email</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isSingle ? (
          <div>
            <p className="mb-2">
              L'indirizzo email inserito per <strong>{warnings[0].label}</strong> potrebbe non essere corretto:
            </p>
            <div className="alert alert-warning py-2 px-3 mb-3">
              <div className="fw-bold font-monospace text-break mb-1">{warnings[0].email}</div>
              <div className="small text-dark">{warnings[0].warningMessage}</div>
            </div>
          </div>
        ) : (
          <div>
            <p className="mb-2">
              Sono state rilevate possibili anomalie negli indirizzi email inseriti:
            </p>
            {warnings.map((w, index) => (
              <div key={index} className="alert alert-warning py-2 px-3 mb-2">
                <div className="small text-muted fw-bold text-uppercase">{w.label}</div>
                <div className="fw-bold font-monospace text-break mb-1">{w.email}</div>
                <div className="small text-dark">{w.warningMessage}</div>
              </div>
            ))}
          </div>
        )}

        <p className="text-muted small mb-0">
          Puoi tornare indietro per modificare l'indirizzo prima di salvare, oppure ignorare la segnalazione e confermare il salvataggio dei dati.
        </p>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <Button
          variant="outline-secondary"
          onClick={handleCorrect}
          data-testid="email-warning-correct-btn"
        >
          <i className="fas fa-arrow-left me-1"></i> Correggi
        </Button>
        <Button
          variant="warning"
          onClick={onIgnoreAndProceed}
          data-testid="email-warning-ignore-btn"
          className="text-dark fw-bold"
        >
          <i className="fas fa-check me-1"></i> Ignora e procedi
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

EmailWarningModal.propTypes = {
  show: PropTypes.bool.isRequired,
  warnings: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      email: PropTypes.string.isRequired,
      warningMessage: PropTypes.string.isRequired,
      suggestedEmail: PropTypes.string,
      fieldId: PropTypes.string,
      selector: PropTypes.string,
    })
  ),
  onCorrect: PropTypes.func.isRequired,
  onIgnoreAndProceed: PropTypes.func.isRequired,
};

export default EmailWarningModal;
